import * as React from "react";
import { Toast, ToastProvider as UiToastProvider, ToastViewport, ToastTitle, ToastDescription } from "@/components/ui/Toast";

interface ToastItem {
  id: string;
  title: string;
  description?: string;
  variant?: "default" | "destructive";
}

const ToastContext = React.createContext<{
  add: (toast: Omit<ToastItem, "id">) => void;
  remove: (id: string) => void;
}>({ add: () => {}, remove: () => {} });

export function useToast() {
  return React.useContext(ToastContext);
}

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = React.useState<ToastItem[]>([]);

  const add = React.useCallback((toast: Omit<ToastItem, "id">) => {
    const id = Math.random().toString(36).slice(2);
    setToasts((prev) => [...prev, { ...toast, id }]);
  }, []);

  const remove = React.useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ add, remove }}>
      <UiToastProvider swipeDirection="right">
        {children}
        {toasts.map((t) => (
          <Toast data-testid="toast-item" key={t.id} variant={t.variant} onOpenChange={(open: boolean) => !open && remove(t.id)}>
            <ToastTitle>{t.title}</ToastTitle>
            {t.description && <ToastDescription>{t.description}</ToastDescription>}
          </Toast>
        ))}
        <ToastViewport />
      </UiToastProvider>
    </ToastContext.Provider>
  );
}
